import cfg from '../../config.json';

/**
 * Canvas 2D fallback blurrer — no WebGPU required.
 * Uses the CSS `filter: blur()` trick via an offscreen canvas.
 */
export class CanvasBlurrer {
  constructor() {
    this._offscreen = document.createElement('canvas');
    this._ctx = this._offscreen.getContext('2d', { willReadFrequently: true });
  }

  async blurFaces(imageData, detections, {
    radius = 21,
    pixelate = false,
    pixelSize = 16,
  } = {}) {
    const { width, height } = imageData;
    const c = this._offscreen;
    const ctx = this._ctx;

    c.width  = width;
    c.height = height;

    ctx.putImageData(imageData, 0, 0);

    const BOX_PADDING = cfg.blur.boxPadding;
    for (const { x1, y1, x2, y2 } of detections) {
      const pw = (x2 - x1) * BOX_PADDING;
      const ph = (y2 - y1) * BOX_PADDING;
      const bx = Math.floor(Math.max(0,      x1 - pw));
      const by = Math.floor(Math.max(0,      y1 - ph));
      const bw = Math.ceil(Math.min(width,   x2 + pw) - bx);
      const bh = Math.ceil(Math.min(height,  y2 + ph) - by);

      if (bw <= 0 || bh <= 0) continue;

      if (pixelate) {
        applyPixelate(ctx, bx, by, bw, bh, pixelSize);
      } else {
        applyBlur(ctx, bx, by, bw, bh, radius);
      }
    }

    return ctx.getImageData(0, 0, width, height);
  }
}

function applyBlur(ctx, x, y, w, h, radius) {
  // Save context state, clip to region, apply CSS blur, restore
  ctx.save();
  ctx.beginPath();
  ctx.rect(x, y, w, h);
  ctx.clip();
  ctx.filter = `blur(${Math.floor(radius / 2)}px)`;
  // Re-draw the clipped region scaled up to exaggerate blur
  const snap = ctx.getImageData(x, y, w, h);
  const tmp = document.createElement('canvas');
  tmp.width = w; tmp.height = h;
  tmp.getContext('2d').putImageData(snap, 0, 0);
  // Draw several times to amplify blur
  for (let i = 0; i < 4; i++) ctx.drawImage(tmp, x, y, w, h);
  ctx.filter = 'none';
  ctx.restore();
}

function applyPixelate(ctx, x, y, w, h, blockSize) {
  const snap = ctx.getImageData(x, y, w, h);
  const { data } = snap;

  for (let by = 0; by < h; by += blockSize) {
    for (let bx = 0; bx < w; bx += blockSize) {
      // Sample centre pixel of block
      const cx = Math.min(bx + Math.floor(blockSize / 2), w - 1);
      const cy = Math.min(by + Math.floor(blockSize / 2), h - 1);
      const si = (cy * w + cx) * 4;
      const r = data[si], g = data[si + 1], b = data[si + 2], a = data[si + 3];

      for (let dy = 0; dy < blockSize && by + dy < h; dy++) {
        for (let dx = 0; dx < blockSize && bx + dx < w; dx++) {
          const di = ((by + dy) * w + (bx + dx)) * 4;
          data[di] = r; data[di + 1] = g; data[di + 2] = b; data[di + 3] = a;
        }
      }
    }
  }
  ctx.putImageData(snap, x, y);
}
